import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Plus, Settings, LogOut, TrendingUp, CheckCircle,
  XCircle, Clock, Send, Search, MoreVertical, Trash2, Eye, Edit, Copy, Plug, FileDown,
  FileSignature, CreditCard
} from "lucide-react";
import Sidebar from "@/components/Sidebar";

const ProposalsList = () => {
  const { user, logout, token, getCachedData, setCachedData, invalidateCache } = useAuth();
  const [proposals, setProposals] = useState(() => getCachedData('proposals') || []);
  const [loading, setLoading] = useState(!getCachedData('proposals'));
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 50;

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadProposals = async () => {
    // Use cache for first page
    if (page === 1) {
      const cached = getCachedData('proposals');
      if (cached && cached.length > 0) {
        setProposals(cached);
        setLoading(false);
        setHasMore(cached.length >= ITEMS_PER_PAGE);
        return;
      }
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/proposals?page=${page}&limit=${ITEMS_PER_PAGE}`, { headers, withCredentials: true });
      if (page === 1) {
        setProposals(response.data);
        setCachedData('proposals', response.data);
      } else {
        setProposals(prev => [...prev, ...response.data]);
      }
      setHasMore(response.data.length === ITEMS_PER_PAGE);
    } catch (error) {
      toast.error("Failed to load proposals");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      setPage(prev => prev + 1);
    }
  };

  const deleteProposal = async (id) => {
    if (!window.confirm("Are you sure you want to delete this proposal?")) return;
    try {
      await axios.delete(`${API}/proposals/${id}`, { headers, withCredentials: true });
      setProposals(proposals.filter(p => p.proposal_id !== id));
      invalidateCache('proposals');
      invalidateCache('stats');
      toast.success("Proposal deleted");
    } catch (error) {
      toast.error("Failed to delete proposal");
    }
  };

  const duplicateProposal = async (proposal) => {
    try {
      const newProposal = {
        title: `${proposal.title} (Copy)`,
        prospect_name: proposal.prospect_name,
        prospect_email: proposal.prospect_email,
        description: proposal.description,
        sections: proposal.sections,
        total_amount: proposal.total_amount
      };
      const response = await axios.post(`${API}/proposals`, newProposal, { headers, withCredentials: true });
      setProposals([response.data, ...proposals]);
      toast.success("Proposal duplicated");
    } catch (error) {
      toast.error("Failed to duplicate proposal");
    }
  };

  const sendProposal = async (id) => {
    try {
      await axios.post(`${API}/proposals/${id}/send`, {}, { headers, withCredentials: true });
      loadProposals();
      toast.success("Proposal sent to prospect");
    } catch (error) {
      toast.error("Failed to send proposal");
    }
  };

  const downloadPDF = async (proposal) => {
    try {
      const response = await axios.get(
        `${API}/proposals/${proposal.proposal_id}/pdf`,
        { headers, withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${proposal.title || 'proposal'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { class: "bg-stone text-foreground", icon: Clock },
      sent: { class: "bg-blue-100 text-blue-800", icon: Send },
      viewed: { class: "bg-purple-100 text-purple-800", icon: Eye },
      accepted: { class: "bg-green-100 text-green-800", icon: CheckCircle },
      declined: { class: "bg-red-100 text-red-800", icon: XCircle }
    };
    const v = variants[status] || variants.draft;
    return (
      <Badge className={`${v.class} capitalize`}>
        <v.icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  // Get contract/payment tracking indicators for accepted proposals
  const getTrackingIndicators = (proposal) => {
    if (proposal.status !== "accepted") return null;
    
    const contractStatus = proposal.docusign_envelope_status;
    const contractEnvelopeId = proposal.docusign_envelope_id;
    const paymentStatus = proposal.payment_status;
    const paymentType = proposal.payment_type;
    const splitProgress = proposal.split_payment_progress;
    
    const contractSigned = contractStatus === "completed";
    const contractSent = !!contractEnvelopeId; // Has envelope ID means it was sent
    const paymentPaid = paymentStatus === "paid";
    const paymentSent = paymentStatus === "pending"; // pending means sent, waiting for payment
    
    // Check if proposal has contract configured
    const hasContract = proposal.docusign_config?.agreement_doc_id;
    // Check if proposal has pricing
    const hasPricing = proposal.sections?.some(s => s.type === "pricing");
    
    // Get contract display state
    const getContractState = () => {
      if (!hasContract) return { text: 'Not Configured', color: 'text-muted-foreground' };
      if (contractSigned) return { text: 'Signed', color: 'text-green-600' };
      if (contractSent) return { text: 'Pending', color: 'text-amber-600' };
      return { text: 'Not Sent', color: 'text-muted-foreground' };
    };
    
    // Get payment display state
    const getPaymentState = () => {
      if (!hasPricing) return { text: 'Not Configured', color: 'text-muted-foreground' };
      
      // Handle split payments specially
      if (paymentType === "split" && splitProgress) {
        const { total, paid, sent } = splitProgress;
        if (paid === total) return { text: `${paid} of ${total} Paid`, color: 'text-green-600' };
        if (paid > 0) return { text: `${paid} of ${total} Paid`, color: 'text-amber-600' };
        if (sent > 0) return { text: `0 of ${total} Paid`, color: 'text-amber-600' };
        return { text: `0 of ${total} Paid`, color: 'text-muted-foreground' };
      }
      
      // Handle subscription - just show status
      if (paymentType === "subscription") {
        if (paymentPaid) return { text: 'Active', color: 'text-green-600' };
        if (paymentSent) return { text: 'Pending', color: 'text-amber-600' };
        return { text: 'Not Sent', color: 'text-muted-foreground' };
      }
      
      // One-time and other types
      if (paymentPaid) return { text: 'Paid', color: 'text-green-600' };
      if (paymentSent) return { text: 'Pending', color: 'text-amber-600' };
      return { text: 'Not Sent', color: 'text-muted-foreground' };
    };
    
    const contractState = getContractState();
    const paymentState = getPaymentState();
    
    return (
      <div className="flex items-center gap-3 mt-1">
        <div className={`flex items-center gap-1 text-xs ${contractState.color}`}>
          <FileSignature className="w-3 h-3" />
          <span>{contractState.text}</span>
        </div>
        <div className={`flex items-center gap-1 text-xs ${paymentState.color}`}>
          <CreditCard className="w-3 h-3" />
          <span>{paymentState.text}</span>
        </div>
      </div>
    );
  };

  const filteredProposals = proposals.filter(p => {
    const matchesFilter = filter === "all" || p.status === filter;
    const matchesSearch = search === "" || 
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.prospect_name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-sand" data-testid="proposals-list">
      {/* Sidebar Component */}
      <Sidebar user={user} logout={logout} />

      {/* Main Content */}
      <main className="md:ml-52 lg:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-forest">Proposals</h1>
            <p className="text-muted-foreground mt-1">{proposals.length} total proposals</p>
          </div>
          <Button asChild className="bg-terracotta hover:bg-terracotta/90" data-testid="new-proposal-btn">
            <Link to="/proposals/new">
              <Plus className="w-4 h-4 mr-2" />
              New Proposal
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search proposals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="viewed">Viewed</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Proposals Grid */}
        {filteredProposals.length === 0 ? (
          <Card className="border-stone">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl text-forest mb-2">No proposals found</h3>
              <p className="text-muted-foreground mb-6">
                {search || filter !== "all" ? "Try adjusting your filters" : "Create your first proposal to get started"}
              </p>
              <Button asChild className="bg-forest hover:bg-forest/90">
                <Link to="/proposals/new">Create Proposal</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredProposals.map((proposal) => (
              <Card key={proposal.proposal_id} className="border-stone hover:border-forest/30 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-sand rounded-lg flex items-center justify-center overflow-hidden">
                        {proposal.logo_url ? (
                          <img 
                            src={proposal.logo_url} 
                            alt="" 
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <FileText 
                          className="w-6 h-6 text-forest" 
                          style={{ display: proposal.logo_url ? 'none' : 'block' }}
                        />
                      </div>
                      <div>
                        <Link 
                          to={`/proposals/${proposal.proposal_id}`}
                          className="font-display text-lg font-medium text-forest hover:underline"
                          data-testid={`proposal-link-${proposal.proposal_id}`}
                        >
                          {proposal.title}
                        </Link>
                        <p className="text-sm text-muted-foreground">{proposal.prospect_name} • {proposal.prospect_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {proposal.total_amount && (
                        <span className="font-mono text-lg text-forest">${proposal.total_amount.toLocaleString()}</span>
                      )}
                      <div className="flex flex-col items-end">
                        {getStatusBadge(proposal.status)}
                        {getTrackingIndicators(proposal)}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`proposal-menu-${proposal.proposal_id}`}>
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/proposals/${proposal.proposal_id}`}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/view/${proposal.proposal_id}`} target="_blank">
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </Link>
                          </DropdownMenuItem>
                          {proposal.status === "draft" && (
                            <DropdownMenuItem onClick={() => sendProposal(proposal.proposal_id)}>
                              <Send className="w-4 h-4 mr-2" />
                              Send to Prospect
                            </DropdownMenuItem>
                          )}
                          {proposal.status !== "draft" && (
                            <DropdownMenuItem onClick={() => downloadPDF(proposal)} data-testid={`download-pdf-${proposal.proposal_id}`}>
                              <FileDown className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => duplicateProposal(proposal)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => deleteProposal(proposal.proposal_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Load More Button */}
            {hasMore && filteredProposals.length >= ITEMS_PER_PAGE && (
              <div className="flex justify-center mt-6">
                <Button 
                  variant="outline" 
                  onClick={loadMore}
                  disabled={loading}
                  className="border-forest text-forest hover:bg-forest hover:text-white"
                >
                  {loading ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProposalsList;
